import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Clock, Target, BookOpen,
  CheckCircle, AlertCircle, Layers, RotateCw, ChevronUp, ChevronDown,
} from 'lucide-react';
import { getStudentAnalyticsPlus1, type StudentAnalytics } from '../services/testService';
import { fetchConceptsPlus1 } from '../services/conceptPlus1Service';
import { getMasteryForConcepts, getStudentConceptState, type StudentConceptState } from '../services/masteryService';
import { getStoredSession } from '../hooks/useAuth';
import { PLUS1_SUBJECTS } from '../types';
import type { ConceptPlus1 } from '../types';

type BackItem = { label: string; sub?: string };

function StatCard({ icon, bg, value, label, description, color, cardKey, flippedCard, onFlip, backItems }: {
  icon: ReactNode; bg: string; value: number; label: string; description: string; color: string;
  cardKey: string; flippedCard: string | null; onFlip: (key: string) => void; backItems: BackItem[];
}) {
  const isFlipped = flippedCard === cardKey;
  return (
    <div
      className="relative h-44 cursor-pointer select-none"
      style={{ perspective: '1000px' }}
      onClick={() => onFlip(cardKey)}
      title={isFlipped ? 'Click to flip back' : backItems.length > 0 ? 'Click to see concepts' : undefined}
    >
      <div
        className="relative w-full h-full transition-transform duration-500"
        style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 glass-card rounded-xl p-5 text-center shadow-sm flex flex-col items-center justify-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          {backItems.length > 0 && (
            <RotateCw className="absolute top-2 right-2 w-3.5 h-3.5 text-gray-300" />
          )}
          <div className={`inline-flex items-center justify-center w-10 h-10 ${bg} rounded-full mb-2`}>
            {icon}
          </div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs font-semibold text-gray-700 mt-0.5">{label}</p>
          <p className="text-xs text-gray-400 mt-1 leading-tight px-1">{description}</p>
        </div>
        {/* Back */}
        <div
          className="absolute inset-0 glass-card rounded-xl shadow-sm flex flex-col overflow-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 flex-shrink-0">
            <span className={`text-xs font-bold ${color}`}>{label}</span>
            <RotateCw className="w-3.5 h-3.5 text-gray-300" />
          </div>
          {backItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-xs text-gray-400 text-center px-3">Nothing here yet</p>
            </div>
          ) : (
            <ul className="flex-1 overflow-y-auto divide-y divide-gray-50 px-1 py-1">
              {backItems.map((item, i) => (
                <li key={i} className="px-2 py-1.5">
                  <p className="text-xs font-medium text-ocean-deep leading-tight">{item.label}</p>
                  {item.sub && <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

type Tab = 'tests' | 'mastery';


// ── Student insight (matches rewise-ui, student-voice language) ───────────────

type InsightLevel = 'secure' | 'building' | 'misconception' | 'exploring' | 'not_started';

interface StudentInsight {
  level: InsightLevel;
  reason: string;
  action: string | null;
  fading: boolean;
  underconfident: boolean;
}

function daysAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${diff}d ago`;
}

function computeStudentInsight(r: StudentConceptState): StudentInsight {
  const fading = r.is_decaying;
  const underconfident = r.underconfident && !r.overconfident;

  if (!r.has_micro && !r.has_simulate && !r.has_apply) {
    return { level: 'not_started', reason: 'You haven\'t explored this concept yet.', action: null, fading: false, underconfident: false };
  }
  if (r.overconfident) {
    const confNote = r.confidence_high > 0
      ? `You marked High confidence ${r.confidence_high > 1 ? `${r.confidence_high} times` : 'once'}`
      : 'You answered with high confidence';
    return {
      level: 'misconception',
      reason: `${confNote} but got it wrong — there's a hidden gap here.`,
      action: 'Revisit the Simulate tab and try again carefully.',
      fading, underconfident: false,
    };
  }
  if (r.simulate_correct === true && (r.apply_max_difficulty === 'Standard' || r.apply_max_difficulty === 'Advanced')) {
    return {
      level: 'secure',
      reason: `You got Simulate right and reached ${r.apply_max_difficulty} in Practice. Great work!`,
      action: fading ? 'You haven\'t revisited this in a while — do a quick review so it doesn\'t slip.' : null,
      fading, underconfident,
    };
  }
  if (r.simulate_correct === true) {
    const practiceNote = r.has_apply ? ` · Practice at ${r.apply_max_difficulty ?? 'Basic'} level so far` : '';
    return {
      level: 'building',
      reason: `You got Simulate right${practiceNote}.`,
      action: !r.has_apply ? 'Try the Applied Practice section to lock this in.' : 'Push to the next difficulty level in Practice.',
      fading, underconfident,
    };
  }
  if (r.micro_correct === true && !r.has_simulate) {
    return {
      level: 'building',
      reason: 'You got the Predict part right — now complete the Simulate tab to go deeper.',
      action: 'Open the Simulate tab for this concept.',
      fading, underconfident,
    };
  }
  if (r.has_simulate && r.simulate_correct === false) {
    const tryNote = r.simulate_attempts > 1 ? ` (tried ${r.simulate_attempts} times)` : '';
    return {
      level: 'exploring',
      reason: `You did Simulate but got it wrong${tryNote}.`,
      action: r.simulate_attempts > 1
        ? 'This one\'s not clicking — ask your teacher for help.'
        : 'That\'s okay — try the Simulate tab again.',
      fading, underconfident,
    };
  }
  if (r.has_micro) {
    return {
      level: 'exploring',
      reason: r.micro_correct === false
        ? 'You tried Story / Predict but got it wrong — the Simulate tab will help.'
        : 'You\'ve explored Story and Predict — keep going with the Simulate tab.',
      action: 'Open the Simulate tab for this concept.',
      fading, underconfident,
    };
  }
  return { level: 'exploring', reason: 'You\'ve started this concept.', action: null, fading, underconfident };
}

const INSIGHT_DISPLAY: Record<InsightLevel, { label: string; dot: string; badge: string }> = {
  secure:        { label: 'Secure',               dot: 'bg-green-500', badge: 'bg-green-100 text-green-800'  },
  building:      { label: 'Building',             dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-800'  },
  misconception: { label: 'Hidden gap — revisit', dot: 'bg-red-500',   badge: 'bg-red-100 text-red-700'      },
  exploring:     { label: 'Exploring',            dot: 'bg-blue-400',  badge: 'bg-blue-100 text-blue-800'    },
  not_started:   { label: 'Not started',          dot: 'bg-gray-300',  badge: 'bg-gray-100 text-gray-500'    },
};

export default function Analytics() {
  const [tab, setTab]               = useState<Tab>('tests');
  const [analytics, setAnalytics]   = useState<StudentAnalytics | null>(null);
  const [concepts, setConcepts]     = useState<ConceptPlus1[]>([]);
  const [masteryMap, setMasteryMap] = useState<Record<number, string>>({});
  const [conceptStates, setConceptStates] = useState<StudentConceptState[]>([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [flippedCard, setFlippedCard] = useState<string | null>(null);
  const toggleCard = (key: string) => setFlippedCard(prev => prev === key ? null : key);
  const [subjectExpanded, setSubjectExpanded] = useState<Set<string>>(new Set());
  function toggleSubject(subj: string) {
    setSubjectExpanded(prev => {
      const next = new Set(prev);
      next.has(subj) ? next.delete(subj) : next.add(subj);
      return next;
    });
  }
  const session = getStoredSession();

  useEffect(() => {
    if (!session?.student_id) return;
    const sid = session.student_id;
    Promise.all([
      getStudentAnalyticsPlus1(sid),
      fetchConceptsPlus1().then(async all => {
        const visible = all.filter(c => c.subject_name !== 'English');
        setConcepts(visible);
        return getMasteryForConcepts(sid, visible.map(c => c.id));
      }),
      getStudentConceptState(sid),
    ]).then(([data, map, states]) => {
      setAnalytics(data);
      setMasteryMap(map);
      setConceptStates(states.filter((r: StudentConceptState) => r.subject_name !== 'English'));
      setIsLoading(false);
    });
  }, [session?.student_id]);

  const masteryTotals = useMemo(() => {
    const t: Record<string, number> = { Strong: 0, Building: 0, 'Needs revisit': 0, 'Not started': 0 };
    concepts.forEach(c => { const l = masteryMap[c.id] || 'Not started'; t[l] = (t[l] || 0) + 1; });
    return t;
  }, [concepts, masteryMap]);

  const bySubject = useMemo(() => {
    const groups: Record<string, { concepts: ConceptPlus1[]; counts: Record<string, number> }> = {};
    concepts.forEach(c => {
      const subj = c.subject_name || PLUS1_SUBJECTS[c.subject_id || 0] || 'Other';
      if (!groups[subj]) groups[subj] = { concepts: [], counts: {} };
      groups[subj].concepts.push(c);
      const label = masteryMap[c.id] || 'Not started';
      groups[subj].counts[label] = (groups[subj].counts[label] || 0) + 1;
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [concepts, masteryMap]);

  const needsRevisit = useMemo(() =>
    concepts.filter(c => masteryMap[c.id] === 'Needs revisit'),
    [concepts, masteryMap]
  );

  const trend: 'up' | 'down' | 'neutral' = useMemo(() => {
    const strong = masteryTotals['Strong'] || 0;
    const revisit = masteryTotals['Needs revisit'] || 0;
    if (strong > 5 && strong > revisit * 2) return 'up';
    if (revisit > strong) return 'down';
    return 'neutral';
  }, [masteryTotals]);

  if (isLoading) {
    return (
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-warm mx-auto mb-4" />
          <p className="text-gray-500 animate-pulse text-sm">Loading your progress...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 space-y-5">

      {/* Hero banner */}
      <div className="relative rounded-2xl p-6 sm:p-8 overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 text-white shadow-lg animate-fade-in-up">
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-7 h-7" />
              <h1 className="text-2xl sm:text-3xl font-bold">My Progress</h1>
            </div>
            <p className="text-purple-100 text-sm">Track your learning journey and achievements</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/20 backdrop-blur-sm text-sm font-semibold flex-shrink-0">
            {trend === 'up'      && <><TrendingUp   className="w-4 h-4" /> Improving</>}
            {trend === 'down'    && <><TrendingDown className="w-4 h-4" /> Time to review</>}
            {trend === 'neutral' && <><Minus        className="w-4 h-4" /> Steady progress</>}
          </div>
        </div>
      </div>

      {/* Mastery stat cards — tap to flip and see concepts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 animate-fade-in-up">
        <StatCard
          icon={<CheckCircle className="w-5 h-5 text-green-600" />} bg="bg-green-50" color="text-green-600"
          value={masteryTotals['Strong'] || 0} label="Strong" description="Strong grasp — can apply confidently"
          cardKey="strong" flippedCard={flippedCard} onFlip={toggleCard}
          backItems={concepts.filter(c => masteryMap[c.id] === 'Strong').map(c => ({ label: c.name, sub: c.subject_name ?? undefined }))}
        />
        <StatCard
          icon={<BookOpen className="w-5 h-5 text-amber-500" />} bg="bg-amber-50" color="text-amber-600"
          value={masteryTotals['Building'] || 0} label="Building" description="Getting there — a bit more practice"
          cardKey="building" flippedCard={flippedCard} onFlip={toggleCard}
          backItems={concepts.filter(c => masteryMap[c.id] === 'Building').map(c => ({ label: c.name, sub: c.subject_name ?? undefined }))}
        />
        <StatCard
          icon={<AlertCircle className="w-5 h-5 text-red-500" />} bg="bg-red-50" color="text-red-600"
          value={masteryTotals['Needs revisit'] || 0} label="Needs Revisit" description="Mastery fading — review soon"
          cardKey="revisit" flippedCard={flippedCard} onFlip={toggleCard}
          backItems={concepts.filter(c => masteryMap[c.id] === 'Needs revisit').map(c => ({ label: c.name, sub: c.subject_name ?? undefined }))}
        />
        <StatCard
          icon={<Layers className="w-5 h-5 text-gray-400" />} bg="bg-gray-50" color="text-gray-500"
          value={masteryTotals['Not started'] || 0} label="Not Started" description="Yet to be explored"
          cardKey="notstarted" flippedCard={flippedCard} onFlip={toggleCard}
          backItems={[]}
        />
      </div>

      {/* Tabs inside glass card */}
      <div className="glass-card rounded-xl shadow-sm overflow-hidden animate-fade-in-up">
        <div className="flex overflow-x-auto gap-1 p-1.5 bg-gray-100 border-b border-gray-200">
          {([['tests', 'Test Performance'], ['mastery', 'Concept Mastery']] as [Tab, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                tab === key ? 'bg-white text-ocean-deep shadow-sm' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {label}
              {key === 'mastery' && needsRevisit.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700">{needsRevisit.length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-5">

          {/* ── TEST PERFORMANCE ── */}
          {tab === 'tests' && (
            <div className="space-y-4">
              {!analytics || analytics.summary.total_tests === 0 ? (
                <div className="text-center py-10">
                  <Target className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium text-sm">No test data yet</p>
                  <Link to="/practice-test" className="text-coral-warm hover:underline mt-2 inline-block text-sm font-medium">
                    Take a practice test →
                  </Link>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { value: analytics.summary.total_tests, label: 'Tests Taken', color: 'text-coral-warm' },
                      { value: `${analytics.summary.avg_score || 0}%`, label: 'Avg Score', color: 'text-green-600' },
                      { value: analytics.summary.concepts_studied, label: 'Concepts', color: 'text-purple-600' },
                      { value: `${analytics.summary.total_time_minutes || 0}m`, label: 'Time Spent', color: 'text-amber-600' },
                    ].map(({ value, label, color }) => (
                      <div key={label} className="text-center p-3 bg-gray-50 rounded-xl">
                        <p className={`text-xl font-bold ${color}`}>{value}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* By difficulty */}
                  {analytics.by_difficulty && analytics.by_difficulty.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-ocean-deep mb-2">By Difficulty</h3>
                      <div className="grid grid-cols-3 gap-2">
                        {(['basic', 'standard', 'advanced'] as const).map(level => {
                          const d = analytics.by_difficulty?.find(x => x.difficulty === level);
                          const styles = { basic: 'bg-green-50 text-green-600 border-green-200', standard: 'bg-orange-50 text-orange-600 border-orange-200', advanced: 'bg-purple-50 text-purple-600 border-purple-200' };
                          return (
                            <div key={level} className={`text-center p-3 rounded-xl border ${styles[level]}`}>
                              <p className="text-xs font-medium capitalize mb-1">{level}</p>
                              <p className="text-xl font-bold text-ocean-deep">{d?.avg_score || 0}%</p>
                              <p className="text-[10px] text-gray-400">{d?.tests_taken || 0} tests</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* By subject */}
                  {analytics.by_subject && analytics.by_subject.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-ocean-deep mb-2">By Subject</h3>
                      <div className="space-y-1.5">
                        {analytics.by_subject.map(s => (
                          <div key={s.subject_id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all">
                            <div>
                              <p className="font-medium text-ocean-deep text-sm">{s.subject_name || PLUS1_SUBJECTS[s.subject_id] || 'Unknown'}</p>
                              <p className="text-[10px] text-gray-400">{s.tests_taken} tests</p>
                            </div>
                            <span className={`text-base font-bold ${s.avg_score >= 70 ? 'text-green-600' : s.avg_score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                              {s.avg_score}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Strong & Weak */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    {analytics.strong_concepts && analytics.strong_concepts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-ocean-deep mb-2 flex items-center gap-1.5">
                          <CheckCircle className="w-4 h-4 text-green-500" /> Mastered
                        </h3>
                        <div className="space-y-1">
                          {analytics.strong_concepts.map(c => (
                            <Link key={c.concept_id} to={`/concept/${c.concept_id}`}
                              className="flex items-center justify-between p-2.5 rounded-lg hover:bg-green-50 transition-colors group">
                              <span className="text-sm text-gray-700 group-hover:text-green-700 truncate pr-2">{c.concept_name}</span>
                              <span className="text-green-600 font-bold text-sm flex-shrink-0">{c.avg_score}%</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                    {analytics.weak_concepts && analytics.weak_concepts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-ocean-deep mb-2 flex items-center gap-1.5">
                          <AlertCircle className="w-4 h-4 text-red-500" /> Needs Work
                        </h3>
                        <div className="space-y-1">
                          {analytics.weak_concepts.map(c => (
                            <Link key={c.concept_id} to={`/concept/${c.concept_id}`}
                              className="flex items-center justify-between p-2.5 rounded-lg hover:bg-red-50 transition-colors group">
                              <span className="text-sm text-gray-700 group-hover:text-red-700 truncate pr-2">{c.concept_name}</span>
                              <span className="text-red-500 font-bold text-sm flex-shrink-0">{c.avg_score}%</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recent activity */}
                  {analytics.recent_activity && analytics.recent_activity.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-ocean-deep mb-2 flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-amber-500" /> Recent Activity
                      </h3>
                      <div className="space-y-1.5">
                        {analytics.recent_activity.slice(0, 5).map((a, i) => (
                          <Link key={i} to={`/concept/${a.concept_id}`}
                            className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all group">
                            <div>
                              <p className="font-medium text-ocean-deep text-sm group-hover:text-coral-warm transition-colors">{a.concept_name}</p>
                              <p className="text-[10px] text-gray-400 capitalize">{a.difficulty} level</p>
                            </div>
                            <span className={`font-bold text-sm ${(a.score / a.total * 100) >= 70 ? 'text-green-600' : 'text-amber-600'}`}>
                              {a.score}/{a.total}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── CONCEPT MASTERY ── */}
          {tab === 'mastery' && (
            <div className="space-y-4">
              {concepts.length === 0 ? (
                <div className="text-center py-10">
                  <Layers className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">No concept data yet</p>
                  <Link to="/concepts" className="text-coral-warm hover:underline mt-2 inline-block text-sm font-medium">
                    Start exploring concepts →
                  </Link>
                </div>
              ) : (
                <>
                  {/* Overall distribution bar */}
                  <div>
                    <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                      <span>{concepts.length} concepts total</span>
                      <span>{concepts.length - (masteryTotals['Not started'] || 0)} started</span>
                    </div>
                    <div className="flex rounded-full overflow-hidden h-3">
                      {(['Strong', 'Building', 'Needs revisit', 'Not started'] as const).map(label => {
                        const pct = ((masteryTotals[label] || 0) / concepts.length) * 100;
                        const colors = { 'Strong': 'bg-green-500', 'Building': 'bg-amber-400', 'Needs revisit': 'bg-red-400', 'Not started': 'bg-gray-200' };
                        return <div key={label} style={{ width: `${pct}%` }} className={colors[label]} />;
                      })}
                    </div>
                  </div>

                  {/* Priority banners */}
                  {(() => {
                    const misconceptionCount  = conceptStates.filter(r => r.overconfident).length;
                    const fadingCount         = conceptStates.filter(r => r.is_decaying && !r.overconfident).length;
                    const underconfidentCount = conceptStates.filter(r => r.underconfident && !r.overconfident).length;
                    const abandonedCount      = conceptStates.filter(r => r.phase_abandoned && !r.overconfident).length;
                    if (!misconceptionCount && !fadingCount && !underconfidentCount && !abandonedCount) return null;
                    return (
                      <div className="flex flex-wrap gap-2">
                        {misconceptionCount > 0 && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                            <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                            <span className="text-xs font-semibold text-red-800">
                              {misconceptionCount} concept{misconceptionCount > 1 ? 's' : ''} with a hidden gap — worth fixing now
                            </span>
                          </div>
                        )}
                        {fadingCount > 0 && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200">
                            <Clock className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                            <span className="text-xs font-semibold text-sky-800">
                              {fadingCount} concept{fadingCount > 1 ? 's' : ''} you haven't revisited in a while
                            </span>
                          </div>
                        )}
                        {abandonedCount > 0 && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200">
                            <span className="text-xs font-semibold text-orange-800">
                              {abandonedCount} concept{abandonedCount > 1 ? 's' : ''} explored but Practice section not tried yet
                            </span>
                          </div>
                        )}
                        {underconfidentCount > 0 && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200">
                            <span className="text-xs font-semibold text-purple-800">
                              You know {underconfidentCount} concept{underconfidentCount > 1 ? 's' : ''} better than you think — trust yourself!
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Subject accordions — collapsed by default, click to see per-concept breakdown */}
                  {(() => {
                    const stateMap = new Map(conceptStates.map(s => [s.concept_id, s]));
                    return bySubject.map(([subj, { concepts: subjConcepts }]) => {
                      const engaged    = subjConcepts.filter(c => stateMap.has(c.id));
                      const isOpen     = subjectExpanded.has(subj);
                      const total      = engaged.length;
                      const secureN    = engaged.filter(c => computeStudentInsight(stateMap.get(c.id)!).level === 'secure').length;
                      const buildingN  = engaged.filter(c => computeStudentInsight(stateMap.get(c.id)!).level === 'building').length;
                      const gapN       = engaged.filter(c => computeStudentInsight(stateMap.get(c.id)!).level === 'misconception').length;
                      const exploringN = total - secureN - buildingN - gapN;

                      return (
                        <div key={subj} className="border border-gray-100 rounded-xl overflow-hidden">
                          <button
                            onClick={() => total > 0 && toggleSubject(subj)}
                            className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${total > 0 ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-gray-700">{subj}</span>
                                <span className="text-xs text-gray-400">{total} of {subjConcepts.length} started</span>
                              </div>
                              {total > 0 ? (
                                <>
                                  <div className="flex rounded-full overflow-hidden h-1.5 bg-gray-100">
                                    {gapN > 0       && <div className="bg-red-400"   style={{ width: `${(gapN / total) * 100}%` }} />}
                                    {secureN > 0    && <div className="bg-green-500" style={{ width: `${(secureN / total) * 100}%` }} />}
                                    {buildingN > 0  && <div className="bg-amber-400" style={{ width: `${(buildingN / total) * 100}%` }} />}
                                    {exploringN > 0 && <div className="bg-blue-300"  style={{ width: `${(exploringN / total) * 100}%` }} />}
                                  </div>
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {gapN > 0       && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">{gapN} Has a gap</span>}
                                    {secureN > 0    && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800">{secureN} Secure</span>}
                                    {buildingN > 0  && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">{buildingN} Building</span>}
                                    {exploringN > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800">{exploringN} Exploring</span>}
                                  </div>
                                </>
                              ) : (
                                <div className="h-1.5 rounded-full bg-gray-100" />
                              )}
                            </div>
                            {total > 0 && (
                              isOpen
                                ? <ChevronUp   className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                          </button>

                          {isOpen && total > 0 && (
                            <ul className="divide-y divide-gray-50 border-t border-gray-100">
                              {engaged.map(c => {
                                const r       = stateMap.get(c.id)!;
                                const insight = computeStudentInsight(r);
                                const display = INSIGHT_DISPLAY[insight.level];
                                const leftBorder = insight.level === 'misconception'
                                  ? 'border-l-4 border-l-red-400'
                                  : insight.fading
                                  ? 'border-l-4 border-l-sky-300'
                                  : 'border-l-4 border-l-transparent';
                                return (
                                  <li key={c.id} className={`px-4 py-3 ${leftBorder}`}>
                                    <div className="flex items-start gap-3">
                                      <span className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${display.dot}`} />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                          <Link to={`/concept/${c.id}`} className="text-sm font-semibold text-ocean-deep hover:underline truncate">
                                            {c.name}
                                          </Link>
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${display.badge}`}>
                                            {display.label}
                                          </span>
                                          {insight.fading && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700 flex-shrink-0">
                                              <Clock className="w-2.5 h-2.5" /> Fading
                                            </span>
                                          )}
                                          <span className="ml-auto flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                                            <Clock className="w-3 h-3" />{daysAgo(r.last_interaction_at)}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-500 leading-relaxed">{insight.reason}</p>
                                        {insight.action && (
                                          <p className={`text-xs font-semibold mt-0.5 ${insight.level === 'misconception' ? 'text-red-600' : insight.level === 'secure' ? 'text-sky-600' : 'text-coral-warm'}`}>
                                            → {insight.action}
                                          </p>
                                        )}
                                        {insight.underconfident && (
                                          <p className="text-xs text-purple-500 mt-0.5">You know this better than you think — keep going!</p>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    });
                  })()}
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </main>
  );
}
